/**
 * @fileoverview Indirect Costs service using axios client and Zod validation.
 * @module features/indirect-costs/api/indirectCostsService
 */

import apiClient from '@/lib/apiClient'
import {
  pageIndirectCostResponseSchema,
  type PageIndirectCostResponse,
  indirectCostEntrySchema,
  type IndirectCostEntryDTO,
} from '../schemas/indirectCostsSchema'

/**
 * Query parameters for listing indirect costs.
 */
export interface ListIndirectCostsParams {
  startDate?: string // YYYY-MM-DD inclusive
  endDate?: string // YYYY-MM-DD exclusive
  category?: 'OPEX' | 'DA'
  page?: number
  size?: number
  sort?: string | string[]
}

/**
 * List indirect costs with optional month filter and pagination.
 * Validates response with Zod and fails fast on errors.
 *
 * @param params Query params (dates, paging)
 * @returns Parsed PageIndirectCostResponse
 * @throws Error when validation fails
 * @example
 * const page = await listIndirectCosts({ startDate: '2025-09-01', endDate: '2025-10-01', page: 0, size: 50 })
 */
export async function listIndirectCosts(
  params: ListIndirectCostsParams
): Promise<PageIndirectCostResponse> {
  const response = await apiClient.get('/indirect-costs', {
    params: {
      // Backend accepts plain page/size/sort query params
      startDate: params.startDate,
      endDate: params.endDate,
      category: params.category,
      page: params.page ?? 0,
      size: params.size ?? 50,
      ...(params.sort ? { sort: params.sort } : {}),
    },
  })
  const parsed = pageIndirectCostResponseSchema.safeParse(response.data)
  if (!parsed.success) {
    throw new Error('API validation failed for /indirect-costs')
  }
  return parsed.data
}

/**
 * Delete an indirect cost by UUID.
 * @param uuid Entry UUID
 */
export async function deleteIndirectCost(uuid: string): Promise<void> {
  await apiClient.delete(`/indirect-costs/${uuid}`)
}

export interface CreateIndirectCostRequest {
  /** Free-form description of the cost */
  description?: string
  /** Inclusive effective start date (YYYY-MM-DD) */
  effectiveFrom?: string
  /** Exclusive effective end date (YYYY-MM-DD) */
  effectiveTo?: string
  /** Linked indirect cost subcategory UUID */
  subcategoryUuid: string
  /** Original amount before conversion (required by backend) */
  originalAmount: number
  /** Original currency code (e.g., EUR, PLN) (required by backend) */
  originalCurrency: string
}

export async function createIndirectCost(
  body: CreateIndirectCostRequest
): Promise<IndirectCostEntryDTO> {
  const res = await apiClient.post('/indirect-costs', body)
  const parsed = indirectCostEntrySchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for POST /indirect-costs')
  return parsed.data
}

export interface UpdateIndirectCostRequest {
  description?: string | null
  reportingAmount?: number
  reportingCurrency?: string
  effectiveFrom?: string
  effectiveTo?: string // exclusive
  subcategoryUuid?: string
  originalAmount?: number | null
  originalCurrency?: string | null
}

/**
 * Update an indirect cost by UUID. Returns the updated entry.
 * Uses PUT to replace fields supported by the backend.
 */
export async function updateIndirectCost(
  uuid: string,
  body: UpdateIndirectCostRequest
): Promise<IndirectCostEntryDTO> {
  const res = await apiClient.put(`/indirect-costs/${uuid}`, body)
  const parsed = indirectCostEntrySchema.safeParse(res.data)
  if (!parsed.success) throw new Error('API validation failed for PUT /indirect-costs/{uuid}')
  return parsed.data
}

/**
 * Response from ensure-recurring endpoint.
 */
export interface EnsureRecurringCostsResponse {
  /** Number of new entries created */
  created: number
}

/**
 * Ensures recurring indirect cost entries exist for subcategories with
 * autoCreateMonthly=true for the specified month. Idempotent.
 *
 * @param yearMonth Month in YYYY-MM format
 * @returns Number of entries created (0 if all already existed)
 * @example
 * const { created } = await ensureRecurringCosts('2026-01')
 */
export async function ensureRecurringCosts(yearMonth: string): Promise<EnsureRecurringCostsResponse> {
  const response = await apiClient.post('/indirect-costs/ensure-recurring', null, {
    params: { yearMonth },
  })
  return response.data as EnsureRecurringCostsResponse
}

export const indirectCostsService = {
  listIndirectCosts,
  deleteIndirectCost,
  createIndirectCost,
  updateIndirectCost,
  ensureRecurringCosts,
}
export default indirectCostsService
